/**
 * useEditLock — manages the full lifecycle of a page editing lock.
 *
 * Responsibilities:
 * - Query lock state on mount (GET)
 * - Acquire lock on first edit (POST)
 * - Refresh lock every 30 seconds while actively editing (PUT)
 * - Release lock on unmount and beforeunload (DELETE)
 * - Handle takeover requests (POST with takeover flag)
 * - Track last edit timestamp to suppress refresh when idle
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  acquirePageLock,
  getPageLockState,
  refreshPageLock,
  releasePageLock,
} from '../../api/pages-api';
import { appConfig } from '../../config';
import { isClientError } from '../../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseEditLockOptions {
  readonly pageId: string | null;
  readonly userId: string;
  readonly displayName: string;
}

export interface UseEditLockResult {
  /** true when another user holds the lock */
  readonly isLocked: boolean;
  /** displayName of lock holder (null if unlocked or self) */
  readonly lockHolder: string | null;
  /** true when current user holds the lock */
  readonly isLockOwner: boolean;
  /** attempt to acquire; returns success */
  readonly acquireLock: () => Promise<boolean>;
  /** explicit release */
  readonly releaseLock: () => void;
  /** force take-over */
  readonly takeOver: () => Promise<boolean>;
  /** signal that an edit has occurred (updates last-edit timestamp for refresh timing) */
  readonly markEditing: () => void;
  /** process an incoming WebSocket lock event */
  readonly handleLockEvent: (event: { detailType: string; payload: Record<string, unknown> }) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REFRESH_INTERVAL_MS = 30_000;
const MAX_CONSECUTIVE_REFRESH_FAILURES = 2;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useEditLock({
  pageId,
  userId,
  // displayName is part of the interface for future use (WebSocket event matching)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  displayName: _displayName,
}: UseEditLockOptions): UseEditLockResult {
  const [isLocked, setIsLocked] = useState(false);
  const [lockHolder, setLockHolder] = useState<string | null>(null);
  const [isLockOwner, setIsLockOwner] = useState(false);

  // Refs for values needed inside intervals/event handlers without re-renders
  const lastEditTimeRef = useRef<number>(0);
  const isLockOwnerRef = useRef(false);
  const pageIdRef = useRef(pageId);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const consecutiveFailuresRef = useRef(0);

  // Keep refs in sync with state
  isLockOwnerRef.current = isLockOwner;
  pageIdRef.current = pageId;

  // -------------------------------------------------------------------------
  // markEditing — called by the consumer on each edit
  // -------------------------------------------------------------------------

  const markEditing = useCallback(() => {
    lastEditTimeRef.current = Date.now();
  }, []);

  // -------------------------------------------------------------------------
  // releaseLock — sends DELETE request and resets state
  // -------------------------------------------------------------------------

  const doRelease = useCallback(async (id: string) => {
    try {
      await releasePageLock(id);
    } catch {
      // Release failures are non-critical — TTL will clean up
    }
  }, []);

  const releaseLock = useCallback(() => {
    if (!pageIdRef.current || !isLockOwnerRef.current) return;
    setIsLockOwner(false);
    isLockOwnerRef.current = false;
    doRelease(pageIdRef.current);
  }, [doRelease]);

  // -------------------------------------------------------------------------
  // acquireLock — POST to acquire; returns true on success
  // -------------------------------------------------------------------------

  const acquireLock = useCallback(async (): Promise<boolean> => {
    if (!pageId) return false;
    if (isLockOwnerRef.current) return true; // already own it

    try {
      const result = await acquirePageLock(pageId);
      if (result.locked && result.userId === userId) {
        setIsLockOwner(true);
        isLockOwnerRef.current = true;
        setIsLocked(false);
        setLockHolder(null);
        lastEditTimeRef.current = Date.now();
        consecutiveFailuresRef.current = 0;
        return true;
      }
      // Unexpected — treat as locked by someone else
      setIsLocked(true);
      setLockHolder(result.displayName ?? null);
      return false;
    } catch (error: unknown) {
      if (isClientError(error) && error.type === 'api' && error.statusCode === 409) {
        // Lock held by another user — parse holder info from error response
        const holderName = (error as any).fields?.displayName ?? (error as any).message ?? 'Another user';
        setIsLocked(true);
        setLockHolder(holderName);
        setIsLockOwner(false);
        isLockOwnerRef.current = false;
        return false;
      }
      // Network error — stay in current state, caller may retry on next edit
      return false;
    }
  }, [pageId, userId]);

  // -------------------------------------------------------------------------
  // takeOver — acquire with takeover flag
  // -------------------------------------------------------------------------

  const takeOver = useCallback(async (): Promise<boolean> => {
    if (!pageId) return false;

    try {
      const result = await acquirePageLock(pageId, true);
      if (result.locked && result.userId === userId) {
        setIsLockOwner(true);
        isLockOwnerRef.current = true;
        setIsLocked(false);
        setLockHolder(null);
        lastEditTimeRef.current = Date.now();
        consecutiveFailuresRef.current = 0;
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [pageId, userId]);

  // -------------------------------------------------------------------------
  // Refresh interval — PUT to extend TTL every 30 seconds while editing
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!pageId) return;

    refreshIntervalRef.current = setInterval(async () => {
      // Only refresh if we own the lock and have edited recently
      if (!isLockOwnerRef.current) return;
      if (!pageIdRef.current) return;

      const timeSinceLastEdit = Date.now() - lastEditTimeRef.current;
      if (timeSinceLastEdit > REFRESH_INTERVAL_MS) return; // idle — skip refresh

      try {
        await refreshPageLock(pageIdRef.current);
        consecutiveFailuresRef.current = 0;
      } catch (error: unknown) {
        consecutiveFailuresRef.current += 1;

        // On 403 or 404, lock was lost (expired or taken)
        if (isClientError(error) && error.type === 'api' && (error.statusCode === 403 || error.statusCode === 404)) {
          setIsLockOwner(false);
          isLockOwnerRef.current = false;
          return;
        }

        // After 2 consecutive network failures, assume lock lost
        if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_REFRESH_FAILURES) {
          setIsLockOwner(false);
          isLockOwnerRef.current = false;
        }
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [pageId]);

  // -------------------------------------------------------------------------
  // Query initial lock state on mount
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!pageId) return;

    let cancelled = false;

    (async () => {
      try {
        const state = await getPageLockState(pageId);
        if (cancelled) return;

        if (state.locked && state.userId && state.userId !== userId) {
          setIsLocked(true);
          setLockHolder(state.displayName ?? null);
        } else if (state.locked && state.userId === userId) {
          // We already hold the lock (e.g. reconnect scenario)
          setIsLockOwner(true);
          isLockOwnerRef.current = true;
        }
      } catch {
        // If query fails, assume page is available — user will get feedback on acquire
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pageId, userId]);

  // -------------------------------------------------------------------------
  // Release on unmount and beforeunload
  // -------------------------------------------------------------------------

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isLockOwnerRef.current || !pageIdRef.current) return;

      // Use sendBeacon for reliability during tab close
      const apiUrl = appConfig.apiUrl.replace(/\/$/, '');

      if (apiUrl) {
        const url = `${apiUrl.replace(/\/$/, '')}/v1/pages/${encodeURIComponent(pageIdRef.current)}/lock`;
        // sendBeacon doesn't support DELETE, so we use a keepalive fetch
        try {
          fetch(url, {
            method: 'DELETE',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
          });
        } catch {
          // Best effort — TTL will expire as fallback
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Release on unmount (route navigation)
      if (isLockOwnerRef.current && pageIdRef.current) {
        releasePageLock(pageIdRef.current).catch(() => {
          // Non-critical — TTL will clean up
        });
        isLockOwnerRef.current = false;
      }
    };
  }, []);

  // -------------------------------------------------------------------------
  // handleLockEvent — process incoming WebSocket lock events
  // -------------------------------------------------------------------------

  const handleLockEvent = useCallback(
    (event: { detailType: string; payload: Record<string, unknown> }) => {
      const { detailType, payload } = event;

      switch (detailType) {
        case 'page.lock.acquired': {
          // Another user acquired the lock
          const eventUserId = payload.userId as string | undefined;
          if (eventUserId && eventUserId !== userId) {
            setIsLocked(true);
            setLockHolder((payload.displayName as string) ?? null);
            setIsLockOwner(false);
            isLockOwnerRef.current = false;
          }
          break;
        }

        case 'page.lock.released': {
          // Lock was released — page is available for editing
          setIsLocked(false);
          setLockHolder(null);
          break;
        }

        case 'page.lock.taken': {
          // Lock was forcibly taken by another user
          const previousUserId = payload.previousUserId as string | undefined;
          if (previousUserId === userId) {
            // We were the previous holder — we lost the lock
            setIsLockOwner(false);
            isLockOwnerRef.current = false;
            setIsLocked(true);
            setLockHolder((payload.newDisplayName as string) ?? null);
          } else {
            // Someone else lost the lock; the new holder took it
            const newUserId = payload.newUserId as string | undefined;
            if (newUserId !== userId) {
              setIsLocked(true);
              setLockHolder((payload.newDisplayName as string) ?? null);
            }
          }
          break;
        }
      }
    },
    [userId],
  );

  return {
    isLocked,
    lockHolder,
    isLockOwner,
    acquireLock,
    releaseLock,
    takeOver,
    markEditing,
    handleLockEvent,
  };
}
